import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { distinctUntilChanged, Subscription } from "rxjs";
import * as _ from "lodash";
import { Capacitor } from "@capacitor/core";

import * as fromApp from "../store/app.reducer";
import * as selectors from "../store/app.selectors";
import { DataStorageService } from "../services/data-storage/data-storage.service";
import * as GroupAppActions from "../store/group-app/groupApp.actions";
import * as StaticPagesActions from "../store/static-pages/staticPages.actions";

@Component({
  selector: "app-custom-page",
  templateUrl: "./custom-page.page.html",
  styleUrls: ["./custom-page.page.scss"],
})
export class CustomPagePage implements OnInit, OnDestroy {
  public loading = true;
  public pageId: string;
  public pageTitle = "";
  public pageIcon = "document-text";
  public pageDescriptionText = "";
  public pageButtons: any[] = [];
  public customPageIconOptions: string[] = [
    "home",
    "cart",
    "list",
    "information",
    "gift",
    "ticket",
    "heart",
    "star-half",
    "trophy",
    "megaphone",
    "reader",
    "map",
    "calendar",
    "tablet-portrait",
    "card",
    "clipboard",
    "document-text",
    "log-out",
    "help-circle",
  ];

  private selectedLanguage: string;
  private moBaseLanguage = "en";
  private groupApp: any;
  private storeInfo: any;
  private staticPagesRequested = false;
  private redirectedToHomepage = false;
  private subscriptions: Subscription[] = [];
  private cookiesConsentReopenTriggered = false;
  private readonly cookiesConsentStorageKey = "mobileorderCookiesConsent";
  private readonly cookiesConsentCustomPagePrefix = "custom-page:";

  constructor(
    private store: Store<fromApp.AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private dataStorageService: DataStorageService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.route.paramMap.subscribe((params) => {
        const pageId = params.get("id");
        if (!_.isEqual(this.pageId, pageId)) {
          this.pageId = _.cloneDeep(pageId);
          this.cookiesConsentReopenTriggered = false;
          this.redirectedToHomepage = false;
          this.buildPageViewModel();
          this.fetchStaticPagesIfNeeded();
          this.triggerCookiesConsentBannerReopenIfNeeded();
        }
      }),
    );

    this.subscriptions.push(
      this.store
        .select("groupApp")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (state && !_.isEqual(this.groupApp, state.groupApp)) {
            this.groupApp = _.cloneDeep(state.groupApp);
            this.initializeBaseLanguage();
            this.buildPageViewModel();
            this.fetchStaticPagesIfNeeded();
            this.triggerCookiesConsentBannerReopenIfNeeded();
          }

          if (
            state &&
            !_.isEqual(this.selectedLanguage, state.selectedLangugage)
          ) {
            this.selectedLanguage = _.cloneDeep(state.selectedLangugage);
            this.buildPageViewModel();
          }
        }),
    );

    this.subscriptions.push(
      this.store
        .select(selectors.getStoreInfo)
        .pipe(distinctUntilChanged())
        .subscribe((storeInfo) => {
          if (storeInfo && !_.isEqual(this.storeInfo, storeInfo)) {
            this.storeInfo = _.cloneDeep(storeInfo);
            this.fetchStaticPagesIfNeeded();
          }
        }),
    );
  }

  ionViewDidEnter() {
    this.cookiesConsentReopenTriggered = false;
    this.triggerCookiesConsentBannerReopenIfNeeded();
  }

  openButtonUrl(button: any) {
    if (!button || !button.url || !_.isString(button.url)) {
      return;
    }

    if (button.url.startsWith("/")) {
      this.router.navigateByUrl(button.url);
      return;
    }

    if (/^https?:\/\//i.test(button.url)) {
      window.open(button.url, "_blank");
    }
  }

  ngOnDestroy(): void {
    _.each(this.subscriptions, (sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  private initializeBaseLanguage() {
    const moBaseLanguageObj =
      this.groupApp && this.groupApp.languages
        ? _.find(this.groupApp.languages, {
            mo_base: true,
          })
        : null;

    if (moBaseLanguageObj && moBaseLanguageObj.code) {
      this.moBaseLanguage = _.cloneDeep(moBaseLanguageObj.code);
    } else {
      this.moBaseLanguage = "en";
    }
  }

  private fetchStaticPagesIfNeeded() {
    if (
      this.staticPagesRequested ||
      !this.pageId ||
      !this.groupApp ||
      !this.groupApp.group
    ) {
      return;
    }

    if (this.hasGroupCustomPages()) {
      return;
    }

    if (!this.storeInfo || !this.storeInfo.store_id) {
      return;
    }

    this.staticPagesRequested = true;

    this.dataStorageService
      .fetchStaticPages(this.storeInfo.store_id, this.groupApp.group)
      .subscribe({
        next: (res: any) => {
          if (
            res &&
            res.success &&
            res.static_pages &&
            _.isArray(res.static_pages.customPages) &&
            res.static_pages.customPages.length > 0 &&
            this.groupApp &&
            !_.has(this.groupApp, "customPages")
          ) {
            const updatedGroupApp: any = _.cloneDeep(this.groupApp || {});
            updatedGroupApp.customPages = _.cloneDeep(
              res.static_pages.customPages,
            );
            this.store.dispatch(
              new GroupAppActions.SetGroupApp(_.cloneDeep(updatedGroupApp)),
            );
          } else {
            this.loading = false;
            this.redirectToHomepage();
          }
        },
        error: () => {
          this.loading = false;
          this.redirectToHomepage();
        },
      });
  }

  private buildPageViewModel() {
    const customPages = this.getGroupCustomPages();

    if (!this.pageId || !_.isArray(customPages)) {
      if (this.staticPagesRequested || this.hasGroupCustomPages()) {
        this.loading = false;
        this.redirectToHomepage();
      }
      return;
    }

    const customPage = _.find(customPages, (item) => {
      return item && item.id === this.pageId && item.active;
    });

    if (!customPage) {
      this.pageTitle = "";
      this.pageIcon = "document-text";
      this.pageDescriptionText = "";
      this.pageButtons = [];
      this.loading = false;
      this.redirectToHomepage();
      return;
    }

    const pageTitle = this.getLocalizedText(customPage.title);
    const pageIcon = this.getValidCustomPageIcon(customPage.icon);
    const pageDescriptionText = this.getLocalizedText(customPage.descriptionText);
    const pageButtons = _.map(
      _.filter(customPage.buttons, (button) => {
        return button && button.id && _.isString(button.url) && button.label;
      }),
      (button) => {
        return {
          id: _.cloneDeep(button.id),
          label: this.getLocalizedText(button.label),
          url: _.cloneDeep(button.url),
        };
      },
    );

    this.pageTitle = _.cloneDeep(pageTitle);
    this.pageIcon = _.cloneDeep(pageIcon);
    this.pageDescriptionText = _.cloneDeep(pageDescriptionText);
    this.pageButtons = _.cloneDeep(pageButtons);
    this.loading = false;

    if (!_.trim(this.pageDescriptionText)) {
      this.redirectToHomepage();
    }
  }

  private getLocalizedText(textObj: any) {
    if (!textObj || !_.isObject(textObj)) {
      return "";
    }

    if (this.selectedLanguage && textObj[this.selectedLanguage]) {
      return _.cloneDeep(textObj[this.selectedLanguage]);
    }

    if (this.moBaseLanguage && textObj[this.moBaseLanguage]) {
      return _.cloneDeep(textObj[this.moBaseLanguage]);
    }

    const firstExistingText = _.find(_.values(textObj), (value) => {
      return !!value;
    });

    return _.cloneDeep(firstExistingText || "");
  }

  private hasGroupCustomPages() {
    return !!(this.groupApp && _.has(this.groupApp, "customPages"));
  }

  private getGroupCustomPages() {
    if (!this.hasGroupCustomPages()) {
      return null;
    }

    if (!_.isArray(this.groupApp.customPages)) {
      return [];
    }

    return _.cloneDeep(this.groupApp.customPages);
  }

  private redirectToHomepage() {
    if (this.redirectedToHomepage) {
      return;
    }

    this.redirectedToHomepage = true;
    this.router.navigateByUrl("/homepage", {
      replaceUrl: true,
    });
  }

  private getValidCustomPageIcon(iconName: string) {
    if (!iconName || !_.isString(iconName)) {
      return "document-text";
    }

    if (!_.includes(this.customPageIconOptions, iconName)) {
      return "document-text";
    }

    return _.cloneDeep(iconName);
  }

  private triggerCookiesConsentBannerReopenIfNeeded() {
    if (
      this.cookiesConsentReopenTriggered ||
      !this.isCookiesConsentReopenAllowedForPlatform() ||
      !this.pageId ||
      !this.groupApp ||
      !this.groupApp.cookiesConsent ||
      !this.groupApp.cookiesConsent.enabled ||
      this.getCookiesConsentNavigationPage() !==
        `${this.cookiesConsentCustomPagePrefix}${this.pageId}`
    ) {
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.removeItem(this.cookiesConsentStorageKey);
    }

    this.cookiesConsentReopenTriggered = true;
    this.store.dispatch(
      new StaticPagesActions.SetCookiesConsentReopenTimestamp(Date.now()),
    );
  }

  private isCookiesConsentReopenAllowedForPlatform() {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    return !!(
      this.groupApp &&
      this.groupApp.cookiesConsent &&
      this.groupApp.cookiesConsent.nativePlatforms === true
    );
  }

  private getCookiesConsentNavigationPage() {
    if (
      this.groupApp &&
      this.groupApp.cookiesConsent &&
      _.isString(this.groupApp.cookiesConsent.navigationPage) &&
      this.groupApp.cookiesConsent.navigationPage
    ) {
      return _.cloneDeep(this.groupApp.cookiesConsent.navigationPage);
    }

    return "";
  }
}
